// Link Library Service — the shared, server-written songLinks library (ITER-122)
// Reads go straight to Firestore (DJs only); every write goes through a callable.

import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { getSongMatchHash, isValidAppleId, isValidSongLinkKey, songLinkKey, type LinkProvider } from '../utils/songLinks';

export { getSongMatchHash };
export type { LinkProvider };

// ============================================
// TYPES
// ============================================

export type IntegrityStatus = 'unchecked' | 'matched' | 'mismatch' | 'verified' | 'error';
export type DurationVerified = 'exact' | 'approximate' | null;

export interface IntegrityFields {
  integrityStatus: IntegrityStatus;
  resolvedTitle?: string | null;
  resolvedArtist?: string | null;
  titleSimilarity?: number | null;
  artistSimilarity?: number | null;
  lastCheckedAt?: string | null;
  checkError?: string | null;
}

export interface LibraryLink extends Partial<IntegrityFields> {
  trackId: string;
  url: string;
  platformName: string;
  songLinkUrl: string | null;
  albumArtUrl: string | null;
  durationMs: number | null;
  durationVerified: DurationVerified;
  note: string;
  setBy: string;
  setAt: string;
  eventId: string | null;
  cleanUses: number;
  wrongSongReports: number;
  lastWrongSongAt: string | null;
}

export interface LibraryLinkStats {
  changeCount: number;
  unsettledStreak: number;
  neverCache: boolean;
}

export interface LibrarySong {
  key: string;
  appleId: string | null;
  isrc: string | null;
  title: string;
  artist: string;
  links: Partial<Record<LinkProvider, LibraryLink>>;
  djIds: string[];
  updatedAt: string;
  linkStats: Partial<Record<LinkProvider, LibraryLinkStats>>;
  blockedTrackIds: Partial<Record<LinkProvider, string[]>>;
}

export type LibraryHistoryAction =
  | 'create' | 'replace' | 'migrate' | 'revert' | 'wrong_song' | 'owner_delete' | 'review_approved';

export interface LibraryHistoryEntry {
  id: string;
  action: LibraryHistoryAction;
  provider: LinkProvider;
  trackId: string;
  previousTrackId: string | null;
  setBy: string;
  eventId: string | null;
  at: string;
  wasFlagged: boolean;
  note: string | null;
  songLinkUrl: string | null;
  albumArtUrl: string | null;
  durationMs: number | null;
  revertedFrom: string | null;
  requestId: string | null;
  deletedByOwner: boolean;
}

export type SongLengthSource = 'apple' | 'tidal' | 'spotify';

export interface LinkSuggestion {
  song: LibrarySong;
  link: LibraryLink;
  provider: LinkProvider;
  match: 'exact' | 'isrc' | 'title';
}

export interface LibrarySongInput {
  title: string;
  artist: string;
  appleId?: string | number | null;
  isrc?: string | null;
}

export interface SaveSongLinkEntry {
  songKey?: string;
  song?: LibrarySongInput;
  link: string;
  songLinkUrl?: string | null;
  albumArtUrl?: string | null;
  durationMs?: number | null;
  durationVerified?: DurationVerified;
  note?: string;
  eventId?: string | null;
  requestId?: string;
  integrity?: IntegrityFields;
}

export type SaveSongLinkResult =
  | { index: number; ok: true; status: 'saved' | 'queued_for_review'; songKey: string; provider: LinkProvider; trackId: string; url: string }
  | { index: number; ok: false; error: string };

export interface SaveSongLinkOutcome {
  error: string | null;
  queuedForReview: boolean;
}

export type WrongSongReportKind = 'library' | 'glitch' | 'request_only' | 'already';

const LINK_PROVIDERS: LinkProvider[] = ['spotify', 'tidal', 'apple'];
const HISTORY_ACTIONS: LibraryHistoryAction[] = ['create', 'replace', 'migrate', 'revert', 'wrong_song', 'owner_delete', 'review_approved'];
const LENGTH_SOURCES: SongLengthSource[] = ['apple', 'tidal', 'spotify'];
const IN_QUERY_CHUNK = 30;
const INTEGRITY_STATUSES: IntegrityStatus[] = ['unchecked', 'matched', 'mismatch', 'verified', 'error'];
const CALLABLE_CHUNK = 100;

// ============================================
// MAPPER (whitelist — unlisted fields are dropped)
// ============================================

function isoOf(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return '';
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function countOf(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 0;
}

function stringArrayOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item: unknown): item is string => typeof item === 'string') : [];
}

function isLinkProvider(value: unknown): value is LinkProvider {
  return typeof value === 'string' && (LINK_PROVIDERS as string[]).includes(value);
}

function docToLibraryLink(raw: unknown): LibraryLink | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as DocumentData;
  if (typeof data.trackId !== 'string' || typeof data.url !== 'string') return null;
  const link: LibraryLink = {
    trackId: data.trackId,
    url: data.url,
    platformName: typeof data.platformName === 'string' ? data.platformName : '',
    songLinkUrl: stringOrNull(data.songLinkUrl),
    albumArtUrl: stringOrNull(data.albumArtUrl),
    durationMs: numberOrNull(data.durationMs),
    durationVerified: data.durationVerified === 'exact' || data.durationVerified === 'approximate' ? data.durationVerified : null,
    note: typeof data.note === 'string' ? data.note : '',
    setBy: typeof data.setBy === 'string' ? data.setBy : '',
    setAt: isoOf(data.setAt),
    eventId: stringOrNull(data.eventId),
    cleanUses: countOf(data.cleanUses),
    wrongSongReports: countOf(data.wrongSongReports),
    lastWrongSongAt: data.lastWrongSongAt ? isoOf(data.lastWrongSongAt) || null : null,
  };
  if (INTEGRITY_STATUSES.includes(data.integrityStatus)) {
    link.integrityStatus = data.integrityStatus;
    link.resolvedTitle = stringOrNull(data.resolvedTitle);
    link.resolvedArtist = stringOrNull(data.resolvedArtist);
    link.titleSimilarity = numberOrNull(data.titleSimilarity);
    link.artistSimilarity = numberOrNull(data.artistSimilarity);
    link.lastCheckedAt = stringOrNull(data.lastCheckedAt);
    link.checkError = stringOrNull(data.checkError);
  }
  return link;
}

export function docToLibrarySong(id: string, data: DocumentData): LibrarySong {
  const rawLinks = (data.links ?? {}) as DocumentData;
  const rawStats = (data.linkStats ?? {}) as DocumentData;
  const rawBlocked = (data.blockedTrackIds ?? {}) as DocumentData;
  const links: LibrarySong['links'] = {};
  const linkStats: LibrarySong['linkStats'] = {};
  const blockedTrackIds: LibrarySong['blockedTrackIds'] = {};
  for (const provider of LINK_PROVIDERS) {
    const link = docToLibraryLink(rawLinks[provider]);
    if (link) links[provider] = link;
    const stats = rawStats[provider];
    if (stats && typeof stats === 'object') {
      linkStats[provider] = {
        changeCount: countOf(stats.changeCount),
        unsettledStreak: countOf(stats.unsettledStreak),
        neverCache: stats.neverCache === true,
      };
    }
    const blocked = stringArrayOf(rawBlocked[provider]);
    if (blocked.length > 0) blockedTrackIds[provider] = blocked;
  }
  return {
    key: id,
    appleId: stringOrNull(data.appleId),
    isrc: stringOrNull(data.isrc),
    title: typeof data.title === 'string' ? data.title : '',
    artist: typeof data.artist === 'string' ? data.artist : '',
    links,
    djIds: stringArrayOf(data.djIds),
    updatedAt: isoOf(data.updatedAt),
    linkStats,
    blockedTrackIds,
  };
}

export function docToHistoryEntry(id: string, data: DocumentData): LibraryHistoryEntry | null {
  if (!HISTORY_ACTIONS.includes(data.action) || !isLinkProvider(data.provider) || typeof data.trackId !== 'string') return null;
  return {
    id,
    action: data.action,
    provider: data.provider,
    trackId: data.trackId,
    previousTrackId: stringOrNull(data.previousTrackId),
    setBy: typeof data.setBy === 'string' ? data.setBy : '',
    eventId: stringOrNull(data.eventId),
    at: isoOf(data.at),
    wasFlagged: data.wasFlagged === true,
    note: stringOrNull(data.note),
    songLinkUrl: stringOrNull(data.songLinkUrl),
    albumArtUrl: stringOrNull(data.albumArtUrl),
    durationMs: numberOrNull(data.durationMs),
    revertedFrom: stringOrNull(data.revertedFrom),
    requestId: stringOrNull(data.requestId),
    deletedByOwner: data.deletedByOwner === true,
  };
}

// ============================================
// SESSION CACHE
// ============================================

const suggestionCache = new Map<string, LinkSuggestion | null>();

// ============================================
// READ
// ============================================

async function songAt(key: string): Promise<LibrarySong | null> {
  const snap = await getDoc(doc(db, 'songLinks', key));
  return snap.exists() ? docToLibrarySong(snap.id, snap.data()) : null;
}

function suggestionFrom(song: LibrarySong | null, provider: LinkProvider, match: LinkSuggestion['match']): LinkSuggestion | null {
  const link = song?.links[provider];
  return song && link ? { song, link, provider, match } : null;
}

async function findLinkSuggestion(song: LibrarySongInput, key: string, provider: LinkProvider): Promise<LinkSuggestion | null> {
  const exact = suggestionFrom(await songAt(key), provider, 'exact');
  if (exact) return exact;
  if (!isValidAppleId(song.appleId)) return null;

  if (song.isrc) {
    const snapshot = await getDocs(query(collection(db, 'songLinks'), where('isrc', '==', song.isrc), limit(5)));
    for (const docSnap of snapshot.docs) {
      if (docSnap.id === key) continue;
      const byIsrc = suggestionFrom(docToLibrarySong(docSnap.id, docSnap.data()), provider, 'isrc');
      if (byIsrc) return byIsrc;
    }
  }

  const titleKey = await songLinkKey({ title: song.title, artist: song.artist });
  return suggestionFrom(await songAt(titleKey), provider, 'title');
}

/**
 * The saved link for a song on one player: its own key first, then (only for songs with an Apple ID)
 * a song with the same ISRC, then the legacy title key. Fallback hits must be confirmed by the DJ.
 */
export async function getLinkSuggestion(song: LibrarySongInput, provider: LinkProvider): Promise<LinkSuggestion | null> {
  const key = await songLinkKey(song);
  const cacheKey = `${key}|${provider}`;
  if (suggestionCache.has(cacheKey)) return suggestionCache.get(cacheKey) ?? null;
  const suggestion = await findLinkSuggestion(song, key, provider);
  suggestionCache.set(cacheKey, suggestion);
  return suggestion;
}

/** Every song this DJ saved, confirmed or imported — one array-contains query, sorted newest first. */
export async function getMyLibrarySongs(djUid: string): Promise<LibrarySong[]> {
  const snapshot = await getDocs(query(collection(db, 'songLinks'), where('djIds', 'array-contains', djUid)));
  return snapshot.docs
    .map(docSnap => docToLibrarySong(docSnap.id, docSnap.data()))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** Newest first. */
export async function getSongLinkHistory(songKey: string): Promise<LibraryHistoryEntry[]> {
  const snapshot = await getDocs(query(collection(db, 'songLinks', songKey, 'history'), orderBy('at', 'desc')));
  return snapshot.docs
    .map(docSnap => docToHistoryEntry(docSnap.id, docSnap.data()))
    .filter((entry): entry is LibraryHistoryEntry => entry !== null);
}

function chunksOf<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let start = 0; start < items.length; start += size) chunks.push(items.slice(start, start + size));
  return chunks;
}

export async function getLibrarySongsByKeys(keys: string[]): Promise<Map<string, LibrarySong>> {
  const validKeys = [...new Set(keys)].filter(isValidSongLinkKey);
  const songs = new Map<string, LibrarySong>();
  const snapshots = await Promise.all(chunksOf(validKeys, IN_QUERY_CHUNK).map(chunk =>
    getDocs(query(collection(db, 'songLinks'), where(documentId(), 'in', chunk)))));
  for (const snapshot of snapshots) {
    for (const docSnap of snapshot.docs) songs.set(docSnap.id, docToLibrarySong(docSnap.id, docSnap.data()));
  }
  return songs;
}

export async function getSongLengths(isrcs: string[]): Promise<Map<string, Partial<Record<SongLengthSource, number>>>> {
  const validIsrcs = [...new Set(isrcs)].filter(isrc => ISRC_PATTERN.test(isrc));
  const lengths = new Map<string, Partial<Record<SongLengthSource, number>>>();
  const snapshots = await Promise.all(chunksOf(validIsrcs, IN_QUERY_CHUNK).map(chunk =>
    getDocs(query(collection(db, 'songLengths'), where(documentId(), 'in', chunk)))));
  for (const snapshot of snapshots) {
    for (const docSnap of snapshot.docs) {
      const stored = (docSnap.data().lengths ?? {}) as DocumentData;
      const known: Partial<Record<SongLengthSource, number>> = {};
      for (const source of LENGTH_SOURCES) {
        const ms = numberOrNull(stored[source]?.ms);
        if (ms !== null) known[source] = ms;
      }
      lengths.set(docSnap.id, known);
    }
  }
  return lengths;
}

/** A hint for the Match Library banner only — the server decides every save. */
export async function getMyLinkEditingRevoked(uid: string): Promise<boolean> {
  try {
    const snap = await getDoc(doc(db, 'linkEditRevocations', uid));
    return snap.exists() && snap.data().revoked === true;
  } catch (err) {
    console.warn('[songLinks] Could not read link-editing status:', err);
    return false;
  }
}

export function searchLibrarySongs(songs: LibrarySong[], searchText: string): LibrarySong[] {
  const q = searchText.toLowerCase().trim();
  if (!q) return songs;
  return songs.filter(song => song.title.toLowerCase().includes(q) || song.artist.toLowerCase().includes(q));
}

// ============================================
// WRITE (callables only)
// ============================================

const ISRC_PATTERN = /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/;

function httpsOrNull(url: string | null | undefined): string | null {
  return url && url.startsWith('https://') && url.length <= 1000 ? url : null;
}

function withoutUndefined<T extends object>(record: T): T {
  return Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined)) as T;
}

// Optional metadata the server would refuse is dropped, so it never costs the DJ the link itself.
// Undefined keys are removed too: the callable encoder would send them as null.
export function cleanSaveEntry(entry: SaveSongLinkEntry): SaveSongLinkEntry {
  const duration = entry.durationMs ? Math.round(entry.durationMs) : null;
  const durationMs = duration && duration >= 1 && duration <= 3_600_000 ? duration : null;
  return withoutUndefined({
    ...entry,
    ...(entry.song ? {
      song: withoutUndefined({
        ...entry.song,
        isrc: entry.song.isrc && ISRC_PATTERN.test(entry.song.isrc) ? entry.song.isrc : null,
      }),
    } : {}),
    ...('songLinkUrl' in entry ? { songLinkUrl: httpsOrNull(entry.songLinkUrl) } : {}),
    ...('albumArtUrl' in entry ? { albumArtUrl: httpsOrNull(entry.albumArtUrl) } : {}),
    ...('durationMs' in entry ? { durationMs, durationVerified: durationMs ? entry.durationVerified ?? null : null } : {}),
    ...(entry.note !== undefined ? { note: entry.note.slice(0, 500) } : {}),
  });
}

/**
 * Save links to the shared library. Throws when the call itself fails;
 * per-entry refusals come back as `ok: false` results for the caller to report.
 */
export async function saveSongLinks(entries: SaveSongLinkEntry[]): Promise<SaveSongLinkResult[]> {
  if (entries.length === 0) return [];
  const callable = httpsCallable<{ entries: SaveSongLinkEntry[] }, { results: SaveSongLinkResult[] }>(functions, 'saveSongLinks');
  const cleaned = entries.map(cleanSaveEntry);
  const results: SaveSongLinkResult[] = [];
  try {
    for (let start = 0; start < cleaned.length; start += CALLABLE_CHUNK) {
      const response = await callable({ entries: cleaned.slice(start, start + CALLABLE_CHUNK) });
      results.push(...response.data.results.map(result => ({ ...result, index: result.index + start })));
    }
  } finally {
    suggestionCache.clear();
  }
  return results;
}

export async function removeLibrarySongs(songKeys: string[]): Promise<number> {
  if (songKeys.length === 0) return 0;
  const callable = httpsCallable<{ songKeys: string[] }, { removed: number }>(functions, 'removeLibrarySongs');
  let removed = 0;
  try {
    for (let start = 0; start < songKeys.length; start += CALLABLE_CHUNK) {
      const response = await callable({ songKeys: songKeys.slice(start, start + CALLABLE_CHUNK) });
      removed += response.data.removed;
    }
  } finally {
    suggestionCache.clear();
  }
  return removed;
}

/** Resolves a Spotify short link on the server. Callable errors (with the server's message) bubble up. */
export async function resolveSpotifyLink(url: string): Promise<{ trackId: string; url: string }> {
  const callable = httpsCallable<{ url: string }, { trackId: string; url: string }>(functions, 'resolveSpotifyLink');
  const response = await callable({ url });
  return response.data;
}

export function firstFailedSave(results: SaveSongLinkResult[]): string | null {
  const failed = results.find((result): result is Extract<SaveSongLinkResult, { ok: false }> => !result.ok);
  return failed ? failed.error : null;
}

/**
 * Saves one link and never throws: resolves to why it failed (or null), and whether the server
 * sent it to the owner instead because link editing is paused. For saves that must not block an approval.
 */
export async function trySaveSongLink(entry: SaveSongLinkEntry): Promise<SaveSongLinkOutcome> {
  try {
    const results = await saveSongLinks([entry]);
    const error = firstFailedSave(results);
    const queuedForReview = results.some(result => result.ok && result.status === 'queued_for_review');
    return { error, queuedForReview };
  } catch (err) {
    console.error('[songLinks] Saving to the link library failed:', err);
    return { error: err instanceof Error && err.message ? err.message : 'unknown error', queuedForReview: false };
  }
}

export const QUEUED_FOR_REVIEW_MESSAGE = 'Linked for this song. Your library edits are paused, so the change was sent to the owner.';

export async function reportWrongSong(requestId: string): Promise<{ kind: WrongSongReportKind }> {
  const callable = httpsCallable<{ requestId: string }, { kind: WrongSongReportKind }>(functions, 'reportWrongSong');
  try {
    const response = await callable({ requestId });
    return response.data;
  } finally {
    suggestionCache.clear();
  }
}

export interface RevertSongLinkInput {
  songKey: string;
  provider: LinkProvider;
  historyId: string;
  eventId?: string;
}

export async function revertSongLink(input: RevertSongLinkInput): Promise<{ trackId: string; url: string }> {
  const callable = httpsCallable<RevertSongLinkInput, { trackId: string; url: string }>(functions, 'revertSongLink');
  try {
    const response = await callable(withoutUndefined(input));
    return response.data;
  } finally {
    suggestionCache.clear();
  }
}

export interface FlagSongLinkInput {
  songKey: string;
  provider: LinkProvider;
  trackId: string;
  historyId?: string;
  reason?: string;
}

export async function flagSongLink(input: FlagSongLinkInput): Promise<{ opened: boolean }> {
  const callable = httpsCallable<FlagSongLinkInput, { opened: boolean }>(functions, 'flagSongLink');
  try {
    const response = await callable(withoutUndefined(input));
    return response.data;
  } finally {
    suggestionCache.clear();
  }
}

/** The library key input for a request's song as the guest picked it — never the song after approval. */
export function librarySongOf(song: {
  title: string;
  artist: string;
  itunesTrackId?: string | null;
  catalogIds?: { apple?: string } | null;
  isrc?: string | null;
}): LibrarySongInput {
  return {
    title: song.title,
    artist: song.artist,
    appleId: song.catalogIds?.apple ?? song.itunesTrackId ?? null,
    isrc: song.isrc ?? null,
  };
}
